import { useState, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { socialService } from '../services/api'
import './EditProfileModal.css'

function EditProfileModal({ user, isOpen, onClose }) {
  const queryClient = useQueryClient()
  const [username, setUsername] = useState(user?.username || '')
  const [displayName, setDisplayName] = useState(user?.display_name || '')
  const [description, setDescription] = useState(user?.description || '')
  const [avatarFile, setAvatarFile] = useState(null)
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || null)
  const [usernameError, setUsernameError] = useState('')
  const fileInputRef = useRef(null)

  const updateProfileMutation = useMutation({
    mutationFn: (profileData) => socialService.updateProfile(user.id, profileData),
    onSuccess: (response) => {
      console.log('[EditProfileModal] Update response:', response)
      // Mettre à jour le cache avec les données retournées par le backend
      if (response?.user) {
        const currentData = queryClient.getQueryData(['social-user-profile', user.id])
        const updatedData = {
          ...currentData,
          user: {
            ...currentData?.user,
            ...response.user
          },
          // Préserver les autres données si elles existent
          is_friend: currentData?.is_friend || response?.is_friend || false,
          is_following: currentData?.is_following || response?.is_following || false,
          followers_count: currentData?.followers_count || response?.followers_count || 0,
          following_count: currentData?.following_count || response?.following_count || 0
        }
        queryClient.setQueryData(['social-user-profile', user.id], updatedData)
        console.log('[EditProfileModal] Cache updated with:', updatedData)
        // Forcer un re-render en invalidant la requête après avoir mis à jour le cache
        // Cela garantit que le composant se met à jour même si le cache n'est pas détecté
        setTimeout(() => {
          queryClient.invalidateQueries(['social-user-profile', user.id])
        }, 100)
      }
      onClose()
    },
    onError: (error) => {
      // Gérer les erreurs de validation du backend
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.detail || 'Erreur lors de la mise à jour'
        if (errorMessage.includes('nom d\'utilisateur') || errorMessage.includes('username')) {
          setUsernameError(errorMessage)
        } else {
          alert(errorMessage)
        }
      } else {
        alert('Erreur lors de la mise à jour du profil')
      }
    }
  })

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      // Vérifier la taille (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('L\'image est trop grande. Taille maximale : 5MB')
        return
      }
      
      // Vérifier le type
      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner une image')
        return
      }

      setAvatarFile(file)
      
      // Créer une preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setAvatarPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUsernameChange = (e) => {
    const value = e.target.value
    // Enlever le @ s'il est présent au début
    const cleanValue = value.replace(/^@+/, '')
    setUsername(cleanValue)
    setUsernameError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Valider le username s'il est fourni
    if (username && username.trim()) {
      const cleanUsername = username.trim()
      // Validation basique : alphanumérique, underscore, tiret, 3-50 caractères
      if (!/^[a-zA-Z0-9_-]{3,50}$/.test(cleanUsername)) {
        setUsernameError('Le nom d\'utilisateur doit contenir entre 3 et 50 caractères (lettres, chiffres, _ ou -)')
        return
      }
    }
    
    let avatarUrl = user?.avatar_url || null
    
    // Si une nouvelle image est sélectionnée, l'uploader
    if (avatarFile) {
      // Pour l'instant, on convertit en base64
      // Dans une vraie application, vous devriez uploader l'image vers un service de stockage (Supabase Storage, S3, etc.)
      const reader = new FileReader()
      reader.onloadend = () => {
        avatarUrl = reader.result
        updateProfileMutation.mutate({
          username: username?.trim() || null,
          display_name: displayName?.trim() || null,
          description: description || null,
          avatar_url: avatarUrl
        })
      }
      reader.readAsDataURL(avatarFile)
    } else {
      updateProfileMutation.mutate({
        username: username?.trim() || null,
        display_name: displayName?.trim() || null,
        description: description || null,
        avatar_url: avatarUrl
      })
    }
  }

  if (!isOpen) return null

  return (
    <div className="edit-profile-modal-overlay" onClick={onClose}>
      <div className="edit-profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edit-profile-modal-header">
          <h2>Modifier le profil</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="edit-profile-form">
          <div className="form-group">
            <label>Photo de profil</label>
            <div className="avatar-upload-section">
              <div 
                className="avatar-preview"
                onClick={() => fileInputRef.current?.click()}
                style={{ 
                  backgroundImage: avatarPreview ? `url(${avatarPreview})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                {!avatarPreview && (user?.email?.charAt(0).toUpperCase() || 'U')}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <button 
                type="button"
                className="change-avatar-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                Changer la photo
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="display_name">Nom d'affichage</label>
            <input
              id="display_name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Votre nom d'affichage (modifiable à tout moment)"
              maxLength={100}
            />
            <small className="form-hint">Ce nom sera visible sur votre profil</small>
          </div>

          <div className="form-group">
            <label htmlFor="username">Nom d'utilisateur @ (unique)</label>
            <div className="username-input-wrapper">
              <span className="username-prefix">@</span>
              <input
                id="username"
                type="text"
                value={username}
                onChange={handleUsernameChange}
                placeholder="nomutilisateur"
                maxLength={50}
                className={usernameError ? 'error' : ''}
              />
            </div>
            {usernameError && (
              <span className="error-message">{usernameError}</span>
            )}
            <small className="form-hint">3-50 caractères (lettres, chiffres, _ ou -). Unique et vérifié pour disponibilité.</small>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Parlez-nous de vous..."
              maxLength={500}
              rows={4}
            />
            <div className="char-count">{description.length}/500</div>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-btn"
              onClick={onClose}
            >
              Annuler
            </button>
            <button 
              type="submit" 
              className="save-btn"
              disabled={updateProfileMutation.isLoading}
            >
              {updateProfileMutation.isLoading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditProfileModal

